import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Search, Phone, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import { BRAND } from '../mock';
import { useCatalog } from '../hooks/useCatalog';

const navItems = [
  { to: '/', label: 'Home' },
  { to: '/products', label: 'Produk', hasMega: true },
  { to: '/articles', label: 'Artikel' },
  { to: '/about', label: 'Tentang' },
  { to: '/contact', label: 'Kontak' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [megaOpen, setMegaOpen] = useState(false);
  const [mobileProductsOpen, setMobileProductsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const { categoryTree } = useCatalog();
  const navigate = useNavigate();
  const location = useLocation();
  const closeTimer = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close mega menu on route change
  useEffect(() => {
    setMegaOpen(false);
  }, [location.pathname, location.search]);

  const openMega = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    setMegaOpen(true);
  };
  const closeMega = (delay = 120) => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setMegaOpen(false), delay);
  };
  const closeMegaNow = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setMegaOpen(false);
  };

  const submit = (e) => {
    e.preventDefault();
    if (search.trim()) navigate(`/products?q=${encodeURIComponent(search.trim())}`);
    setOpen(false);
  };

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-white/95 backdrop-blur-md border-b border-neutral-200 shadow-sm'
          : 'bg-white/80 backdrop-blur'
      }`}
      onMouseLeave={() => closeMega(80)}
    >
      {/* Top utility bar */}
      <div className="hidden md:flex relative justify-between items-center text-xs text-neutral-500 px-6 lg:px-12 py-2 border-b border-neutral-100">
        <div className="flex items-center gap-2">
          <Phone className="w-3.5 h-3.5" />
          <span>Hotline 24/7: +62 21 5050 8800</span>
        </div>
        <span
          data-testid="topbar-beta-notice"
          className="absolute left-1/2 -translate-x-1/2 font-bold text-px-red tracking-wide animate-pulse"
        >
          Website Tahap Uji Coba – Hanya untuk Penggunaan Internal
        </span>
        <div className="flex items-center gap-5">
          <span>Garansi Resmi 2 Tahun</span>
          <span>Service Nasional</span>
          <span>ID · EN</span>
        </div>
      </div>

      <div className="relative max-w-[1400px] mx-auto px-5 lg:px-12 flex items-center justify-between h-[68px]">
        <Link to="/" className="flex items-center gap-2">
          <img
            src={BRAND.logoUrl}
            alt="PowerX"
            className="h-10 w-auto object-contain"
          />
        </Link>
        <span
          data-testid="topbar-beta-notice-mobile"
          className="md:hidden absolute left-1/2 -translate-x-1/2 text-center text-[10px] font-bold leading-tight text-px-red animate-pulse pointer-events-none"
        >
          Website Tahap Uji Coba
          <br />
          Hanya untuk Penggunaan Internal
        </span>

        <nav className="hidden lg:flex items-center gap-9">
          {navItems.map((n) =>
            n.hasMega ? (
              <div
                key={n.to}
                className="relative"
                onMouseEnter={openMega}
                onMouseLeave={() => closeMega()}
              >
                <NavLink
                  to={n.to}
                  className={({ isActive }) =>
                    `nav-link inline-flex items-center gap-1 text-sm font-medium tracking-wide ${
                      isActive ? 'active text-neutral-900' : 'text-neutral-700'
                    }`
                  }
                  data-testid="nav-produk"
                >
                  {n.label} <ChevronDown className="w-3.5 h-3.5" />
                </NavLink>
              </div>
            ) : (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === '/'}
                onMouseEnter={closeMegaNow}
                className={({ isActive }) =>
                  `nav-link text-sm font-medium tracking-wide ${
                    isActive ? 'active text-neutral-900' : 'text-neutral-700'
                  }`
                }
              >
                {n.label}
              </NavLink>
            )
          )}
        </nav>

        <div className="flex items-center gap-2">
          <form
            onSubmit={submit}
            className="hidden md:flex items-center bg-neutral-100 rounded-full px-3 py-2 w-56 focus-within:ring-2 focus-within:ring-red-200"
          >
            <Search className="w-4 h-4 text-neutral-500" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Cari produk..."
              className="bg-transparent outline-none text-sm ml-2 w-full placeholder:text-neutral-500"
            />
          </form>

          <Button
            asChild
            className="hidden md:inline-flex bg-px-red hover:bg-red-700 text-white rounded-full px-5"
          >
            <Link to="/contact">Konsultasi</Link>
          </Button>

          <button
            onClick={() => setOpen(!open)}
            className="lg:hidden p-2 rounded-md hover:bg-neutral-100"
            aria-label="toggle menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mega menu (desktop) */}
      {megaOpen && (
        <div
          className="hidden lg:block absolute left-0 right-0 top-full bg-white border-t border-neutral-200 shadow-xl"
          data-testid="mega-menu"
          onMouseEnter={openMega}
          onMouseLeave={() => closeMega(80)}
        >
          <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-8 grid grid-cols-3 lg:grid-cols-4 gap-x-8 gap-y-8">
            {categoryTree
              .filter((c) => c.count > 0 || c.alwaysVisible)
              .map((c) => (
              <div key={c.slug}>
                <Link
                  to={`/products?category=${c.slug}`}
                  onClick={() => setMegaOpen(false)}
                  className="block mb-3 group"
                >
                  <p className="text-[10px] tracking-[0.25em] text-px-red font-semibold mb-1">
                    {c.subtitle.toUpperCase()}
                  </p>
                  <h4 className="font-display text-xl font-bold text-neutral-900 group-hover:text-px-red transition-colors">
                    {c.title}
                  </h4>
                </Link>
                {c.subcategories.filter((s) => s.count > 0 || s.alwaysVisible).length > 0 ? (
                  <ul className="space-y-1.5">
                    {c.subcategories
                      .filter((s) => s.count > 0 || s.alwaysVisible)
                      .map((s) => (
                      <li key={s.slug}>
                        <Link
                          to={`/products?category=${c.slug}&sub=${s.slug}`}
                          onClick={() => setMegaOpen(false)}
                          className="block text-sm text-neutral-600 hover:text-px-red transition-colors py-1"
                          data-testid={`mega-${s.slug}`}
                        >
                          {s.title}
                        </Link>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-neutral-500 leading-relaxed">
                    {c.description}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Mobile menu */}
      {open && (
        <div className="lg:hidden border-t border-neutral-200 bg-white max-h-[calc(100vh-68px)] overflow-y-auto">
          <div className="px-5 py-4 flex flex-col gap-1">
            {navItems.map((n) =>
              n.hasMega ? (
                <div key={n.to}>
                  <button
                    onClick={() => setMobileProductsOpen((v) => !v)}
                    className="w-full flex items-center justify-between py-3 px-2 rounded-md text-base font-medium text-neutral-800 hover:bg-neutral-50"
                  >
                    <span>{n.label}</span>
                    <ChevronDown
                      className={`w-4 h-4 transition-transform ${
                        mobileProductsOpen ? 'rotate-180' : ''
                      }`}
                    />
                  </button>
                  {mobileProductsOpen && (
                    <div className="pl-3 pb-2 space-y-1">
                      <Link
                        to="/products"
                        onClick={() => setOpen(false)}
                        className="block py-2 px-2 rounded-md text-sm font-semibold text-px-red"
                      >
                        Semua Produk →
                      </Link>
                      {categoryTree
                        .filter((c) => c.count > 0 || c.alwaysVisible)
                        .map((c) => (
                        <div key={c.slug} className="pt-1">
                          <Link
                            to={`/products?category=${c.slug}`}
                            onClick={() => setOpen(false)}
                            className="block py-1.5 px-2 rounded-md text-sm font-semibold text-neutral-900"
                          >
                            {c.title}
                          </Link>
                          <div className="pl-2 space-y-0.5">
                            {c.subcategories
                              .filter((s) => s.count > 0 || s.alwaysVisible)
                              .map((s) => (
                              <Link
                                key={s.slug}
                                to={`/products?category=${c.slug}&sub=${s.slug}`}
                                onClick={() => setOpen(false)}
                                className="block py-1 px-2 rounded-md text-sm text-neutral-600"
                              >
                                {s.title}
                              </Link>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <NavLink
                  key={n.to}
                  to={n.to}
                  onClick={() => setOpen(false)}
                  end={n.to === '/'}
                  className={({ isActive }) =>
                    `py-3 px-2 rounded-md text-base font-medium ${
                      isActive
                        ? 'text-px-red bg-red-50'
                        : 'text-neutral-800 hover:bg-neutral-50'
                    }`
                  }
                >
                  {n.label}
                </NavLink>
              )
            )}

            <form onSubmit={submit} className="flex items-center bg-neutral-100 rounded-full px-3 py-2 mt-2">
              <Search className="w-4 h-4 text-neutral-500" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Cari produk..."
                className="bg-transparent outline-none text-sm ml-2 w-full"
              />
            </form>
            <Button
              asChild
              className="mt-3 bg-px-red hover:bg-red-700 text-white rounded-full"
            >
              <Link to="/contact" onClick={() => setOpen(false)}>
                Konsultasi Gratis
              </Link>
            </Button>
          </div>
        </div>
      )}
    </header>
  );
}
